/**
 * useSaveHandler - Handles saving all pending changes (copies, uploads, reorders).
 */

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import type { UseQueryResult, QueryClient } from '@tanstack/react-query';
import {
  reorderProductImages,
  copyImageToChannel,
  linkAssetToChannel,
  deleteChannelImage,
} from '@/lib/api/products';
import type { ChannelOrderState, PendingCopy, PendingUpload } from './types';
import type { PendingDeleteEntry, PendingReplacement } from './usePendingDeletes';

interface UseSaveHandlerParams {
  productId: string;
  pendingDeletes: PendingDeleteEntry[];
  pendingReplacements: PendingReplacement[];
  pendingCopies: PendingCopy[];
  pendingUploads: PendingUpload[];
  channelOrders: Map<string, ChannelOrderState>;
  changedChannelKeys: Set<string>;
  clearAll: () => void;
  assetsQuery: UseQueryResult;
  queryClient: QueryClient;
}

export function useSaveHandler({
  productId,
  pendingDeletes,
  pendingReplacements,
  pendingCopies,
  pendingUploads,
  channelOrders,
  changedChannelKeys,
  clearAll,
  assetsQuery,
  queryClient,
}: UseSaveHandlerParams) {
  const [saving, setSaving] = useState(false);

  const handleSaveAll = useCallback(async () => {
    setSaving(true);
    try {
      // 1. Plain deletes (skip entries that have a replacement — those are handled in step 2)
      const replacedLinkIds = new Set(pendingReplacements.map(r => r.oldLinkId));
      const plainDeletes = pendingDeletes.filter(d => !replacedLinkIds.has(d.linkId));
      const deleteResults = await processDeletes(productId, plainDeletes);

      // 2. Replacements: DELETE old slot, then POST new image to slot
      const replaceResults = await processReplacements(productId, pendingReplacements);

      // 3. Copies + Uploads
      const copyResults = await processCopies(productId, pendingCopies);
      const uploadResults = await processUploads(productId, pendingUploads);

      // 3. Reorders (also for channels marked changed via empty slot drops)
      const reorderResults = await processReorders(productId, channelOrders, changedChannelKeys, queryClient);

      showResultToasts(copyResults, uploadResults, reorderResults, { success: deleteResults.success + replaceResults.success, errors: deleteResults.errors + replaceResults.errors });

      const totalSuccess = copyResults.success + uploadResults.success + reorderResults.success + deleteResults.success + replaceResults.success;
      const totalErrors = copyResults.errors + uploadResults.errors + reorderResults.errors + deleteResults.errors + replaceResults.errors;

      if (totalSuccess > 0 && totalErrors === 0) {
        clearAll();
        assetsQuery.refetch();
      } else if (totalErrors > 0 && totalSuccess > 0) {
        assetsQuery.refetch();
      }
    } finally {
      setSaving(false);
    }
  }, [productId, pendingDeletes, pendingReplacements, pendingCopies, pendingUploads, channelOrders, changedChannelKeys, clearAll, assetsQuery, queryClient]);

  const handleCancelChanges = useCallback(() => {
    clearAll();
    assetsQuery.refetch();
  }, [clearAll, assetsQuery]);

  return { saving, handleSaveAll, handleCancelChanges };
}

async function processDeletes(productId: string, deletes: PendingDeleteEntry[]) {
  let success = 0, errors = 0;
  // Only process deletes that don't have a replacement (replacements handled separately)
  for (const entry of deletes) {
    try {
      const result = await deleteChannelImage(productId, entry.channelId, entry.slot);
      if (result.success) { success++; }
      else { errors++; toast.error(i18n.t('productImages.deleteError', { slot: entry.slot })); }
    } catch {
      errors++;
      toast.error(i18n.t('productImages.deleteError', { slot: entry.slot }));
    }
  }
  return { success, errors };
}

async function processReplacements(productId: string, replacements: PendingReplacement[]) {
  let success = 0, errors = 0;
  for (const r of replacements) {
    try {
      // Step 1: Delete old image from slot
      const delResult = await deleteChannelImage(productId, r.channelId, r.slot);
      if (!delResult.success) {
        errors++;
        toast.error(`Ersetzen fehlgeschlagen (Slot ${r.slot}): Löschen`);
        continue;
      }
      // Step 2: Link new image to channel (at same slot)
      const linkResult = await linkAssetToChannel(productId, r.channelId, r.newAssetId, r.slot);
      if (linkResult.success) {
        success++;
      } else {
        errors++;
        toast.error(`Ersetzen fehlgeschlagen (Slot ${r.slot}): Verlinken`);
      }
    } catch {
      errors++;
      toast.error(`Ersetzen fehlgeschlagen (Slot ${r.slot})`);
    }
  }
  return { success, errors };
}

async function processCopies(productId: string, copies: PendingCopy[]) {
  let success = 0, errors = 0;
  for (const pending of copies) {
    try {
      const result = await copyImageToChannel(productId, pending.targetChannelId, {
        source_asset_id: pending.sourceAssetId,
        source_channel_id: pending.sourceChannelId,
      });
      if (result.success) { success++; }
      else {
        errors++;
        if (result.error?.includes('bereits')) {
          toast.warning(`Bild bereits in "${pending.targetChannelKey}" vorhanden`);
        }
      }
    } catch (err: unknown) {
      errors++;
      const apiError = err as { response?: { data?: { error?: string } } };
      if (apiError?.response?.data?.error?.includes('bereits')) {
        toast.warning(`Bild bereits in "${pending.targetChannelKey}" vorhanden`);
      }
    }
  }
  return { success, errors };
}

async function processUploads(productId: string, uploads: PendingUpload[]) {
  let success = 0, errors = 0;
  for (const pending of uploads) {
    try {
      // insertAtIndex (0-based) → slot (1-based)
      const targetSlot = pending.insertAtIndex != null ? pending.insertAtIndex + 1 : undefined;
      const result = await linkAssetToChannel(productId, pending.channelId, pending.assetId, targetSlot);
      if (result.success) { success++; }
      else {
        errors++;
        toast.error(`Link fehlgeschlagen: ${result.error}`);
      }
    } catch (err: unknown) {
      errors++;
      const apiError = err as { response?: { data?: { error?: string } } };
      toast.error(`Link fehlgeschlagen: ${apiError?.response?.data?.error || 'Unbekannter Fehler'}`);
    }
  }
  return { success, errors };
}

async function processReorders(
  productId: string,
  channelOrders: Map<string, ChannelOrderState>,
  changedChannelKeys: Set<string>,
  queryClient: QueryClient,
) {
  let success = 0, errors = 0;
  for (const [channelKey, state] of channelOrders.entries()) {
    const orderChanged = JSON.stringify(state.originalOrder) !== JSON.stringify(state.currentOrder);
    if (!orderChanged && !changedChannelKeys.has(channelKey)) continue;
    try {
      // Composite IDs (channelKey::link_id) → echte link_ids extrahieren
      const linkIds = state.currentOrder.map(id => {
        const s = String(id);
        const sep = s.indexOf('::');
        return sep >= 0 ? s.slice(sep + 2) : s;
      });
      const result = await reorderProductImages(
        productId, linkIds, state.channelId || undefined
      );
      if (result.success) {
        success++;
        queryClient.invalidateQueries({
          queryKey: ['channel-history', productId, state.channelId],
        });
      } else { errors++; }
    } catch { errors++; }
  }
  return { success, errors };
}

function showResultToasts(
  copy: { success: number; errors: number },
  upload: { success: number; errors: number },
  reorder: { success: number; errors: number },
  del: { success: number; errors: number },
) {
  const totalSuccess = copy.success + upload.success + reorder.success + del.success;
  const totalErrors = copy.errors + upload.errors + reorder.errors + del.errors;

  if (totalSuccess > 0 && totalErrors === 0) {
    const messages: string[] = [];
    if (del.success > 0) messages.push(`${del.success} Bild${del.success !== 1 ? 'er' : ''} entfernt`);
    if (upload.success > 0) messages.push(`${upload.success} Bild${upload.success !== 1 ? 'er' : ''} hochgeladen`);
    if (copy.success > 0) messages.push(`${copy.success} Bild${copy.success !== 1 ? 'er' : ''} kopiert`);
    if (reorder.success > 0) messages.push(`${reorder.success} Kanal${reorder.success !== 1 ? '/Kanäle' : ''} sortiert`);
    toast.success(messages.join(', '));
  } else if (totalErrors > 0) {
    toast.error(i18n.t('toast.error.savingMultiple', { count: totalErrors }));
  }
}
